"use client"

import * as React from "react"
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import {
  Users,
  Briefcase,
  Building2,
  UserCircle,
  Plus,
  Search,
  Clock,
  ArrowRight,
  Settings,
  CheckSquare,
  MessageSquare,
  Mail,
  Home,
  ArrowLeft,
  Zap,
  UserPlus,
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { useNavigate } from "@tanstack/react-router"
import { useGetCandidates, useGetCompanies, useGetContacts, useGetJobs } from "openapi/api/endpoints"

// Types
type Category = "all" | "candidates" | "jobs" | "companies" | "contacts" | "actions"
type EntityType = "candidate" | "job" | "contact" | "company"
type PaletteMode = "search" | "entity-select"

type SearchableEntity = {
  id: string
  type: EntityType
  name: string
  subtitle?: string
  metadata?: Record<string, string>
}

interface QuickAction {
  id: string
  label: string
  description?: string
  icon: string
  shortcut?: string
  keywords?: string[]
  href?: string
  actionType: "navigate" | "create-job" | "create-contact" | "add-task" | "add-comment"
}

// Icon mapping
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  "plus": Plus,
  "check-square": CheckSquare,
  "message-square": MessageSquare,
  "search": Search,
  "home": Home,
  "users": Users,
  "briefcase": Briefcase,
  "building": Building2,
  "settings": Settings,
  "mail": Mail,
  "user": UserCircle,
  "user-plus": UserPlus,
}

const CATEGORY_TO_ENTITY: Record<Category, EntityType[]> = {
  all: ["candidate", "job", "company", "contact"],
  candidates: ["candidate"],
  jobs: ["job"],
  companies: ["company"],
  contacts: ["contact"],
  actions: [],
}

// Quick actions data
const QUICK_ACTIONS: QuickAction[] = [
  {
    id: "create-job",
    actionType: "create-job",
    label: "Create New Job",
    description: "Post a new job requisition",
    icon: "plus",
    shortcut: "C J",
    keywords: ["new", "job", "requisition", "post", "create"],
  },
  {
    id: "create-contact",
    actionType: "create-contact",
    label: "Create New Contact",
    description: "Add a new contact to your network",
    icon: "user-plus",
    shortcut: "C C",
    keywords: ["new", "contact", "person", "create", "add"],
  },
  {
    id: "add-task-to-candidate",
    actionType: "add-task",
    label: "Add Task to Candidate",
    description: "Create a task for any candidate",
    icon: "check-square",
    shortcut: "T",
    keywords: ["task", "todo", "reminder", "follow-up"],
  },
  {
    id: "add-comment-to-candidate",
    actionType: "add-comment",
    label: "Add Comment to Candidate",
    description: "Add notes or feedback to any candidate",
    icon: "message-square",
    shortcut: "N",
    keywords: ["comment", "note", "feedback", "review"],
  },
  {
    id: "start-search",
    actionType: "navigate",
    label: "Start AI Search",
    description: "Find candidates with AI-powered search",
    icon: "search",
    href: "/searches/new",
    keywords: ["search", "find", "ai", "candidates"],
  },
  {
    id: "go-dashboard",
    actionType: "navigate",
    label: "Go to Dashboard",
    description: "View your dashboard overview",
    icon: "home",
    href: "/dashboard",
    keywords: ["dashboard", "home", "overview"],
  },
  {
    id: "go-candidates",
    actionType: "navigate",
    label: "View All Candidates",
    description: "Browse your candidate pipeline",
    icon: "users",
    href: "/candidates",
    keywords: ["candidates", "talent", "pipeline"],
  },
  {
    id: "go-jobs",
    actionType: "navigate",
    label: "View All Jobs",
    description: "Manage your job requisitions",
    icon: "briefcase",
    href: "/jobs",
    keywords: ["jobs", "requisitions", "positions"],
  },
  {
    id: "go-community",
    actionType: "navigate",
    label: "Go to Community",
    description: "View contacts and organizations",
    icon: "building",
    href: "/community",
    keywords: ["community", "contacts", "companies", "organizations"],
  },
  {
    id: "go-community-contacts",
    actionType: "navigate",
    label: "View Contacts",
    description: "Browse your contacts list",
    icon: "users",
    href: "/community/contacts",
    keywords: ["contacts", "people", "network"],
  },
  {
    id: "go-community-organizations",
    actionType: "navigate",
    label: "View Organizations",
    description: "Browse your organizations",
    icon: "building",
    href: "/community/companies",
    keywords: ["organizations", "companies", "clients"],
  },
  {
    id: "go-tasks",
    actionType: "navigate",
    label: "View Tasks",
    description: "Manage your tasks and to-dos",
    icon: "check-square",
    href: "/ops/tasks",
    keywords: ["tasks", "todos", "reminders"],
  },
  {
    id: "go-settings",
    actionType: "navigate",
    label: "Settings",
    description: "Configure your account",
    icon: "settings",
    href: "/settings",
    keywords: ["settings", "preferences", "account"],
  },
]

// Job sub-actions for navigation
const JOB_SUB_ACTIONS = [
  { id: "overview", label: "Today", description: "Open tasks and recent activity" },
  { id: "pipeline", label: "Pipeline", description: "View candidate pipeline" },
  { id: "search", label: "Search", description: "Search candidates for this job" },
]

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const [search, setSearch] = React.useState("")
  const [category, setCategory] = React.useState<Category>("all")
  const [mode, setMode] = React.useState<PaletteMode>("search")
  const [pendingAction, setPendingAction] = React.useState<QuickAction | null>(null)
  const [recentSearches, setRecentSearches] = React.useState<string[]>([])
  const [effectiveSearchQuery, setEffectiveSearchQuery] = React.useState("")

  const {
    data: candidatesResponse
  } = useGetCandidates({
    search_str: effectiveSearchQuery || undefined,
  })

  const { data:jobs} = useGetJobs()

  const { data: companies } = useGetCompanies()

  const { data:contacts } = useGetContacts()

  React.useEffect(() => {
    // debounce optional later; keep it simple first
    setEffectiveSearchQuery(search)
  }, [search])


  // Reset state when closing
  React.useEffect(() => {
    if (!open) {
      setSearch("")
      setCategory("all")
      setMode("search")
      setPendingAction(null)
    }
  }, [open])

  // Load recent searches from localStorage
  React.useEffect(() => {
    const stored = localStorage.getItem("ats-recent-searches")
    if (stored) {
      setRecentSearches(JSON.parse(stored))
    }
  }, [])

  const saveRecentSearch = (term: string) => {
    if (!term.trim()) return
    const updated = [term, ...recentSearches.filter((s) => s !== term)].slice(0, 5)
    setRecentSearches(updated)
    localStorage.setItem("ats-recent-searches", JSON.stringify(updated))
  }

  // Search entities
  const searchEntities = React.useCallback((query: string, types: EntityType[]): SearchableEntity[] => {
    const results: SearchableEntity[] = []
    const lowerQuery = query.toLowerCase()

    if (types.includes("candidate")) {
      const candidateResults = (candidatesResponse?.candidates ?? [])
        .filter(c =>
          c.full_name?.toLowerCase().includes(lowerQuery) ||
          c.headline?.toLowerCase().includes(lowerQuery) ||
          c.linkedin_profile_url?.toLowerCase().includes(lowerQuery)
        )
        .slice(0, 5)
        .map(c => ({
          id: c.candidate_id as string,
          type: "candidate" as const,
          name: c.full_name ?? '',
          subtitle: c.headline || "",
          metadata: {
            email: c.headline || "",
            location: c.location || "",
          },
        }))

      results.push(...candidateResults)
    }

    if (types.includes("job")) {
      const jobResults = (jobs??[])
        .filter(j =>
          j.title.toLowerCase().includes(lowerQuery) ||
          (j as {company_name?:string}).company_name?.toLowerCase().includes(lowerQuery) ||
          j.department?.toLowerCase().includes(lowerQuery)
        )
        .slice(0, 5)
        .map(j => ({
          id: j.id,
          type: "job" as const,
          name: j.title,
          subtitle: `${(j as {company_name?:string}).company_name} - ${j.department}`,
          metadata: {
            status: j.status??'',
            location: j.location??'',
          },
        }))
      results.push(...jobResults)
    }

    if (types.includes("company")) {
      const companyResults = (companies?.items ?? [])
        .filter(c =>
          c.name?.toLowerCase().includes(lowerQuery) ||
          c.website?.toLowerCase().includes(lowerQuery) ||
          c.industry?.toLowerCase().includes(lowerQuery)
        )
        .slice(0, 5)
        .map(c => ({
          id: c.id,
          type: "company" as const,
          name: c.name,
          subtitle: c.industry || c.website || "",
          metadata: {
            domain: c.website || "",
            location: c.location || "",
          },
        }))
    
      results.push(...companyResults)
    }

    if (types.includes("contact")) {
      const contactsResults = (contacts?? [])
        .filter(c =>
          c.first_name?.toLowerCase().includes(lowerQuery) ||
          c.last_name?.toLowerCase().includes(lowerQuery) ||
          c.company_name?.toLowerCase().includes(lowerQuery) ||
          c.email?.toLowerCase().includes(lowerQuery)
        )
        .slice(0, 5)
        .map(c => ({
          id: c.id,
          type: "contact" as const,
          name: `${c.first_name} ${c.last_name}`,
          subtitle: `${c.title} @${c.company_name}`,
          metadata: {
            email: c.email || "",
            phone: c.phone || "",
          },
        }))
    
      results.push(...contactsResults)
    }
    
    

    return results
  }, [candidatesResponse,companies?.items,jobs])

  // Filter quick actions based on search
  const filteredQuickActions = React.useMemo(() => {
    if (!search) return QUICK_ACTIONS
    const lowerSearch = search.toLowerCase()
    return QUICK_ACTIONS.filter(action =>
      action.label.toLowerCase().includes(lowerSearch) ||
      action.description?.toLowerCase().includes(lowerSearch) ||
      action.keywords?.some(k => k.toLowerCase().includes(lowerSearch))
    )
  }, [search])

  // Get search results
  const searchResults = React.useMemo(() => {
    if (!search && mode !== "entity-select") {
      return { candidates: [], jobs: [], contacts: [], companies: [] }
    }

    const query = search || ""
    const types= (mode === "entity-select" ? ["candidate"] : CATEGORY_TO_ENTITY[category]) as EntityType[]
    const entities = searchEntities(query, types)

    return {
      candidates: entities.filter(e => e.type === "candidate"),
      jobs: entities.filter(e => e.type === "job"),
      contacts: entities.filter(e => e.type === "contact"),
      companies: entities.filter(e => e.type === "company"),
    }
  }, [search, category, mode, searchEntities])

  // Handle entity selection
  const handleSelect = (type: string, id: string, searchTerm?: string, subAction?: string) => {
    if (searchTerm) saveRecentSearch(searchTerm)
    onOpenChange(false)
    setSearch("")

    switch (type) {
      case "candidate":
        navigate({ to: "/candidates", search: { candidateId: id } })
        break
      case "job":
        navigate({ to: subAction ? `/jobs/${id}/${subAction}` : `/jobs/${id}/overview` })
        break
      case "company":
        navigate({ to: "/community/companies", search: { companyId: id } })
        break
      case "contact":
        navigate({ to: `/persons/${id}` })
        break
    }
  }

  // Handle quick action selection
  const handleActionSelect = (action: QuickAction) => {
    // Actions that need entity selection
    if ((action.actionType === "add-task" || action.actionType === "add-comment")) {
      setPendingAction(action)
      setMode("entity-select")
      setSearch("")
      return
    }

    // Navigate actions
    if (action.actionType === "navigate" && action.href) {
      navigate({ to: action.href })
      onOpenChange(false)
      saveRecentSearch(action.label)
      return
    }

    // Create actions
    if (action.actionType === "create-job") {
      navigate({ to: "/jobs", search: { createJob: true } })
      onOpenChange(false)
      return
    }

    if (action.actionType === "create-contact") {
      navigate({ to: "/community/contacts", search: { createContact: true } })
      onOpenChange(false)
      return
    }
  }

  // Handle entity selection in entity-select mode
  const handleEntitySelect = (entity: SearchableEntity) => {
    if (!pendingAction) return

    navigate({
      to: "/candidates",
      search: {
        candidateId: entity.id,
        commentMode: pendingAction.actionType === "add-comment" ? "create" : undefined,
      },
    })
    onOpenChange(false)
    saveRecentSearch(`${pendingAction.label} - ${entity.name}`)
  }



  const handleBack = () => {
    setMode("search")
    setPendingAction(null)
    setSearch("")
  }

  // Visibility flags
  const isEntitySelectMode = mode === "entity-select"
  const showCandidates = (category === "all" || category === "candidates") && !isEntitySelectMode
  const showJobs = (category === "all" || category === "jobs") && !isEntitySelectMode
  const showCompanies = (category === "all" || category === "companies") && !isEntitySelectMode
  const showContacts = (category === "all" || category === "contacts") && !isEntitySelectMode
  const showActions = category === "all" || category === "actions"

  const hasAnyResults = React.useMemo(() => {
    if (isEntitySelectMode) return searchResults.candidates.length > 0

    return (
      (showActions && filteredQuickActions.length > 0) ||
      (showCandidates && searchResults.candidates.length > 0) ||
      (showJobs && searchResults.jobs.length > 0) ||
      (showCompanies && searchResults.companies.length > 0) ||
      (showContacts && searchResults.contacts.length > 0)
    )
  }, [
    isEntitySelectMode,
    searchResults,
    showActions,
    filteredQuickActions,
    showCandidates,
    showJobs,
    showCompanies,
    showContacts,
  ])


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 max-w-2xl w-[95vw] sm:w-full overflow-hidden">
        <Command className="rounded-lg" shouldFilter={false}>
          {/* Header for entity select mode */}
          {isEntitySelectMode && pendingAction && (
            <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/50">
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              <span className="text-sm font-medium">{pendingAction.label}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Select a candidate</span>
            </div>
          )}

          <CommandInput
            placeholder={isEntitySelectMode ? "Search for a candidate..." : "Search or type a command..."}
            value={search}
            onValueChange={setSearch}
            className="h-12 sm:h-11"
          />

          {/* Category Tabs */}
          {!isEntitySelectMode && (
            <div className="flex gap-1 px-2 sm:px-3 py-2 border-b overflow-x-auto scrollbar-hide">
              {[
                { key: "all", label: "All" },
                { key: "actions", label: "Actions", icon: Zap },
                { key: "candidates", label: "Candidates", icon: Users },
                { key: "jobs", label: "Jobs", icon: Briefcase },
                { key: "companies", label: "Companies", icon: Building2 },
                { key: "contacts", label: "Contacts", icon: UserCircle },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setCategory(tab.key as Category)}
                  className={cn(
                    "px-2.5 sm:px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 sm:gap-1.5 whitespace-nowrap flex-shrink-0",
                    category === tab.key
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  {tab.icon && <tab.icon className="h-3 w-3" />}
                  <span className="hidden xs:inline sm:inline">{tab.label}</span>
                  {tab.key === "all" && <span className="xs:hidden sm:hidden">{tab.label}</span>}
                </button>
              ))}
            </div>
          )}

          <CommandList className="max-h-[50vh] sm:max-h-[400px]">
            {search && !hasAnyResults && (
              <div className="py-6 text-center px-4">
                <Search className="h-8 w-8 sm:h-10 sm:w-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">
                  No results found for "{search}"
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {isEntitySelectMode ? "Search for a candidate to continue" : "Try searching for candidates, jobs, or actions"}
                </p>
              </div>
            )}


            {/* Entity Select Mode */}
            {isEntitySelectMode && (
              <CommandGroup heading="Select Candidate">
                {searchResults.candidates.map((candidate) => (
                  <CommandItem
                    key={candidate.id}
                    onSelect={() => handleEntitySelect(candidate)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2 sm:gap-3 w-full min-w-0">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Users className="h-4 w-4 text-brand-ink" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{candidate.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{candidate.subtitle}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Normal Search Mode */}
            {!isEntitySelectMode && (
              <>
                {/* Recent Searches */}
                {!search && recentSearches.length > 0 && (
                  <CommandGroup heading="Recent Searches">
                    {recentSearches.map((term, index) => (
                      <CommandItem key={index} onSelect={() => setSearch(term)} className="cursor-pointer">
                        <Clock className="mr-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="truncate">{term}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {/* Quick Actions */}
                {showActions && filteredQuickActions.length > 0 && (
                  <CommandGroup heading={search ? "Actions" : "Quick Actions"}>
                    {filteredQuickActions.slice(0, search ? 10 : 6).map((action) => {
                      const Icon = iconMap[action.icon] || Plus
                      return (
                        <CommandItem
                          key={action.id}
                          onSelect={() => handleActionSelect(action)}
                          className="cursor-pointer"
                        >
                          <div className="flex items-center gap-2 sm:gap-3 w-full min-w-0">
                            <div className={cn(
                              "h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0",
                              action.actionType === "add-task" ? "bg-blue-500/10" :
                                action.actionType === "add-comment" ? "bg-green-500/10" :
                                  action.actionType === "create-job" ? "bg-purple-500/10" :
                                    "bg-primary/10"
                            )}>
                              <Icon className={cn(
                                "h-4 w-4",
                                action.actionType === "add-task" ? "text-blue-500" :
                                  action.actionType === "add-comment" ? "text-green-500" :
                                    action.actionType === "create-job" ? "text-purple-500" :
                                      "text-brand-ink"
                              )} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{action.label}</p>
                              {action.description && (
                                <p className="text-xs text-muted-foreground truncate">{action.description}</p>
                              )}
                            </div>
                            {action.shortcut && (
                              <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono flex-shrink-0 hidden sm:block">
                                {action.shortcut}
                              </kbd>
                            )}
                            <ArrowRight className="ml-auto h-3 w-3 text-muted-foreground flex-shrink-0" />
                          </div>
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                )}

                {/* Candidates */}
                {search && showCandidates && searchResults.candidates.length > 0 && (
                  <>
                    <CommandSeparator />
                    <CommandGroup heading="Candidates">
                      {searchResults.candidates.slice(0, category === "candidates" ? 10 : 3).map((candidate) => (
                        <div key={candidate.id}>
                          <CommandItem
                            onSelect={() => handleSelect("candidate", candidate.id, search)}
                            className="cursor-pointer"
                          >
                            <div className="flex items-center gap-2 sm:gap-3 w-full min-w-0">
                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Users className="h-4 w-4 text-brand-ink" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{candidate.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{candidate.subtitle}</p>
                              </div>
                              <span className="text-xs text-muted-foreground hidden sm:block flex-shrink-0">
                                {candidate.metadata?.location}
                              </span>
                            </div>
                          </CommandItem>
                          {/* Candidate sub-actions */}
                          {category === "candidates" && (
                            <div className="ml-11 space-y-0.5 mb-2">
                              <CommandItem
                                onSelect={() => {
                                  navigate({
                                    to: "/candidates",
                                    search: { candidateId: candidate.id, commentMode: "create" },
                                  })
                                  onOpenChange(false)
                                }}
                                className="cursor-pointer py-1.5 text-sm"
                              >
                                <MessageSquare className="mr-2 h-3 w-3 text-green-500" />
                                <span>Add Comment</span>
                              </CommandItem>
                              <CommandItem
                                onSelect={() => {
                                  navigate({ to: "/candidates", search: { candidateId: candidate.id } })
                                  onOpenChange(false)
                                }}
                                className="cursor-pointer py-1.5 text-sm"
                              >
                                <CheckSquare className="mr-2 h-3 w-3 text-blue-500" />
                                <span>Add Task</span>
                              </CommandItem>
                            </div>
                          )}
                        </div>
                      ))}
                    </CommandGroup>
                    {category === "all" && searchResults.candidates.length > 3 && (
                      <CommandItem
                        onSelect={() => setCategory("candidates")}
                        className="cursor-pointer justify-center text-xs text-muted-foreground"
                      >
                        View all {searchResults.candidates.length} candidates
                        <ArrowRight className="ml-1 h-3 w-3" />
                      </CommandItem>
                    )}
                  </>
                )}

                {/* Jobs */}
                {search && showJobs && searchResults.jobs.length > 0 && (
                  <>
                    <CommandSeparator />
                    <CommandGroup heading="Jobs">
                      {searchResults.jobs.slice(0, category === "jobs" ? 10 : 3).map((job) => (
                        <div key={job.id}>
                          <CommandItem
                            onSelect={() => handleSelect("job", job.id, search)}
                            className="cursor-pointer"
                          >
                            <div className="flex items-center gap-2 sm:gap-3 w-full min-w-0">
                              <div className="h-8 w-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                                <Briefcase className="h-4 w-4 text-blue-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{job.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{job.subtitle}</p>
                              </div>
                              <Badge
                                variant={job.metadata?.status === "active" ? "default" : "secondary"}
                                className="text-[10px] flex-shrink-0"
                              >
                                {job.metadata?.status}
                              </Badge>
                            </div>
                          </CommandItem>
                          {/* Job sub-actions (tabs) */}
                          {category === "jobs" && (
                            <div className="ml-11 space-y-0.5 mb-2">
                              {JOB_SUB_ACTIONS.map((subAction) => (
                                <CommandItem
                                  key={`${job.id}-${subAction.id}`}
                                  onSelect={() => handleSelect("job", job.id, search, subAction.id)}
                                  className="cursor-pointer py-1.5 text-sm"
                                >
                                  <ArrowRight className="mr-2 h-3 w-3 text-muted-foreground" />
                                  <span>{subAction.label}</span>
                                  <span className="ml-2 text-xs text-muted-foreground hidden sm:inline">{subAction.description}</span>
                                </CommandItem>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </CommandGroup>
                    {category === "all" && searchResults.jobs.length > 3 && (
                      <CommandItem
                        onSelect={() => setCategory("jobs")}
                        className="cursor-pointer justify-center text-xs text-muted-foreground"
                      >
                        View all {searchResults.jobs.length} jobs
                        <ArrowRight className="ml-1 h-3 w-3" />
                      </CommandItem>
                    )}
                  </>
                )}

                {/* Companies */}
                {search && showCompanies && searchResults.companies.length > 0 && (
                  <>
                    <CommandSeparator />
                    <CommandGroup heading="Companies">
                      {searchResults.companies.slice(0, category === "companies" ? 10 : 3).map((company) => (
                        <CommandItem
                          key={company.id}
                          onSelect={() => handleSelect("company", company.id, search)}
                          className="cursor-pointer"
                        >
                          <div className="flex items-center gap-2 sm:gap-3 w-full min-w-0">
                            <div className="h-8 w-8 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                              <Building2 className="h-4 w-4 text-emerald-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{company.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{company.subtitle}</p>
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}

                {/* Contacts */}
                {search && showContacts && searchResults.contacts.length > 0 && (
                  <>
                    <CommandSeparator />
                    <CommandGroup heading="Contacts">
                      {searchResults.contacts.slice(0, category === "contacts" ? 10 : 3).map((contact) => (
                        <CommandItem
                          key={contact.id}
                          onSelect={() => handleSelect("contact", contact.id, search)}
                          className="cursor-pointer"
                        >
                          <div className="flex items-center gap-2 sm:gap-3 w-full min-w-0">
                            <div className="h-8 w-8 rounded-full bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                              <UserCircle className="h-4 w-4 text-orange-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{contact.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{contact.subtitle}</p>
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </>
            )}
          </CommandList>

          {/* Footer */}
          <div className="border-t px-3 py-2 flex items-center justify-between text-xs text-muted-foreground">
            <div className="hidden sm:flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">↑↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">↵</kbd>
                select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">esc</kbd>
                {isEntitySelectMode ? "back" : "close"}
              </span>
            </div>
            <span className="sm:hidden text-muted-foreground">
              {isEntitySelectMode ? "Select a candidate" : "Tap to select"}
            </span>
            <span className="hidden sm:flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">⌘K</kbd>
              to open
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
